// src/pages/UploadPage.js
import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { WEDDING_CONFIG } from '../config';

const UploadPage = () => {
  const [formData, setFormData] = useState({
    guestName: '',
    caption: '',
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file types
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      return validTypes.includes(file.type);
    });

    if (validFiles.length !== files.length) {
      alert('Some files were skipped. Only JPG, JPEG, and PNG are allowed.');
    }

    // Validate file sizes (10MB each)
    const validSizedFiles = validFiles.filter(file => {
      const maxSize = 10 * 1024 * 1024; // 10MB
      return file.size <= maxSize;
    });

    if (validSizedFiles.length !== validFiles.length) {
      alert('Some files were skipped. Maximum file size is 10MB.');
    }

    setSelectedFiles(prev => [...prev, ...validSizedFiles]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const maxSize = 10 * 1024 * 1024;
      return validTypes.includes(file.type) && file.size <= maxSize;
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.guestName.trim()) {
      setMessage({ type: 'error', text: 'Please enter your name' });
      return;
    }

    if (selectedFiles.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one photo' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });
    
    try {
      // ✅ CHANGED: Import from cloudinaryService
      const { uploadPhoto } = await import('../services/cloudinaryService');
      
      let successCount = 0;
      let failCount = 0;
      let lastError = null;
      
      // Upload files one by one
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        setUploadProgress(Math.round(((i + 1) / selectedFiles.length) * 100));
        
        console.log(`Uploading file ${i + 1}/${selectedFiles.length}:`, file.name);
        
        const result = await uploadPhoto(
          file,
          formData.guestName,
          formData.caption
        );
        
        if (result.success) {
          successCount++;
          console.log('Upload successful:', result);
        } else {
          failCount++;
          lastError = result.message;
          console.error('Upload failed:', result.message);
        }
      }
      
      // Show results
      if (successCount === selectedFiles.length) {
        setMessage({
          type: 'success',
          text: `🎉 All ${successCount} photo(s) uploaded successfully! They will appear in the gallery after admin approval.`,
        });
        
        // Reset form
        setFormData({ guestName: '', caption: '' });
        setSelectedFiles([]);
      } else if (successCount > 0) {
        setMessage({
          type: 'warning',
          text: `⚠️ ${successCount} photo(s) uploaded, ${failCount} failed. Error: ${lastError}`,
        });
      } else {
        setMessage({
          type: 'error',
          text: `❌ Upload failed: ${lastError || 'Unknown error'}`,
        });
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({
        type: 'error',
        text: `Failed to upload: ${error.message}`,
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div style={styles.page}>
      <Navbar />
      
      {/* Fixed Background Image - ONLY shows for glass sections */}
      <div style={styles.backgroundImage}></div>
          
      <section style={styles.hero}>
        <h1 style={styles.heroTitle}>Share Your Moments</h1>
        <p style={styles.heroSubtitle}>Upload your photos from our special day</p>
      </section>

      <section style={styles.section}>
        <div style={styles.glassCard}>
          <form onSubmit={handleSubmit} style={styles.form}>
            
            {/* Guest Name */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Your Name *</label>
              <input
                type="text"
                name="guestName"
                value={formData.guestName}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="Enter your name"
                required
                disabled={uploading}
              />
            </div>

            {/* Caption */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Caption (Optional)</label>
              <textarea
                name="caption"
                value={formData.caption}
                onChange={handleInputChange}
                style={styles.textarea}
                placeholder="Share a memory or message..."
                rows="3"
                disabled={uploading}
              />
            </div>

            {/* File Upload */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Upload Photos</label>
              <div
                style={styles.dropzone}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  multiple
                  onChange={handleFileSelect}
                  style={styles.fileInput}
                  id="file-upload"
                  disabled={uploading}
                />
                <label htmlFor="file-upload" style={styles.dropzoneLabel}>
                  <span style={styles.cameraIcon}>📷</span>
                  <p style={styles.dropzoneText}>Click to upload or drag and drop</p>
                  <p style={styles.dropzoneHint}>PNG, JPG, JPEG (Max 10MB each)</p>
                </label>
              </div>
            </div>

            {/* Selected Files Preview */}
            {selectedFiles.length > 0 && (
              <div style={styles.previewSection}>
                <h3 style={styles.previewTitle}>Selected Photos ({selectedFiles.length})</h3>
                <div style={styles.previewGrid}>
                  {selectedFiles.map((file, index) => (
                    <div key={index} style={styles.previewCard}>
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Preview ${index + 1}`}
                        style={styles.previewImage}
                      />
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        style={styles.removeButton}
                        disabled={uploading}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {uploading && (
              <div style={styles.progressSection}>
                <p style={styles.progressText}>Uploading... {uploadProgress}%</p>
                <div style={styles.progressBar}>
                  <div style={{...styles.progressFill, width: `${uploadProgress}%`}}></div>
                </div>
              </div>
            )}

            {/* Message */}
            {message.text && (
              <div style={{
                ...styles.message,
                ...(message.type === 'success' && styles.messageSuccess),
                ...(message.type === 'error' && styles.messageError),
                ...(message.type === 'warning' && styles.messageWarning),
              }}>
                {message.text}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                ...(uploading && styles.submitButtonDisabled),
              }}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : '📸 Upload Photos'}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  // Background Image - Only visible in glass sections (hidden behind hero gradient)
  backgroundImage: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url(${WEDDING_CONFIG.images.upload})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    zIndex: -1,
  },
  hero: {
    height: '100vh',
    background: `linear-gradient(135deg, ${WEDDING_CONFIG.colors.primary}, ${WEDDING_CONFIG.colors.secondary})`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    textAlign: 'center',
    marginTop: '60px',
    padding: '2rem',
  },
  heroTitle: {
    fontSize: '3rem',
    fontFamily: "'Great Vibes', 'Playfair Display', serif",
    marginBottom: '1rem',
  },
  heroSubtitle: {
    fontSize: '1.2rem',
    fontWeight: '300',
  },
  section: {
    padding: '4rem 2rem',
    flex: '1',
  },
  // Frosted Glass Card Effect
  glassCard: {
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(0.5px)',
    WebkitBackdropFilter: 'blur(0.5px)',
    borderRadius: '30px',
    padding: '4rem',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 16px 64px rgba(0, 0, 0, 0.1)',
    maxWidth: '800px',
    width: '100%',
    margin: '0 auto',
  },
  container: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  form: {
    background: 'transparent',
    padding: '0',
  },
  formGroup: {
    marginBottom: '2rem',
  },
  label: {
    display: 'block',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#eee',
    marginBottom: '0.5rem',
  },
  input: {
    width: '100%',
    padding: '1rem',
    fontSize: '1rem',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '10px',
    background: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    transition: 'all 0.3s',
    outline: 'none',
    fontFamily: 'inherit',
    color: '#333',
  },
  textarea: {
    width: '100%',
    padding: '1rem',
    fontSize: '1rem',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '10px',
    background: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    transition: 'all 0.3s',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    color: '#333',
  },
  dropzone: {
    position: 'relative',
    border: `3px dashed ${WEDDING_CONFIG.colors.primary}`,
    borderRadius: '15px',
    padding: '3rem',
    textAlign: 'center',
    background: `${WEDDING_CONFIG.colors.light}30`,
    transition: 'all 0.3s',
    cursor: 'pointer',
  },
  fileInput: {
    display: 'none',
  },
  dropzoneLabel: {
    cursor: 'pointer',
  },
  cameraIcon: {
    fontSize: '3rem',
    display: 'block',
    marginBottom: '1rem',
  },
  dropzoneText: {
    fontSize: '1.1rem',
    color: '#eee',
    marginBottom: '0.5rem',
  },
  dropzoneHint: {
    fontSize: '0.9rem',
    color: '#eee',
  },
  previewSection: {
    marginTop: '2rem',
  },
  previewTitle: {
    fontSize: '1.2rem',
    color: WEDDING_CONFIG.colors.primary,
    marginBottom: '1rem',
  },
  previewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '1rem',
  },
  previewCard: {
    position: 'relative',
    borderRadius: '10px',
    overflow: 'hidden',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  previewImage: {
    width: '100%',
    height: '150px',
    objectFit: 'cover',
  },
  removeButton: {
    position: 'absolute',
    top: '5px',
    right: '5px',
    background: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    cursor: 'pointer',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressSection: {
    marginTop: '2rem',
  },
  progressText: {
    textAlign: 'center',
    fontSize: '1rem',
    color: '#666',
    marginBottom: '0.5rem',
  },
  progressBar: {
    width: '100%',
    height: '10px',
    background: '#eee',
    borderRadius: '10px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: `linear-gradient(90deg, ${WEDDING_CONFIG.colors.primary}, ${WEDDING_CONFIG.colors.secondary})`,
    transition: 'width 0.3s',
  },
  message: {
    padding: '1rem',
    borderRadius: '10px',
    marginTop: '1rem',
    textAlign: 'center',
  },
  messageSuccess: {
    background: '#d4edda',
    color: '#155724',
  },
  messageError: {
    background: '#f8d7da',
    color: '#721c24',
  },
  messageWarning: {
    background: '#fff3cd',
    color: '#856404',
  },
  submitButton: {
    width: '100%',
    padding: '1.2rem',
    background: `linear-gradient(135deg, ${WEDDING_CONFIG.colors.primary}, ${WEDDING_CONFIG.colors.secondary})`,
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.3s',
    marginTop: '2rem',
  },
  submitButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};

export default UploadPage;